import { type ReactNode, useEffect } from 'react';

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  /** Caps the panel height and lets a scrolling body engage, keeping header/footer pinned. */
  scrollBody?: boolean;
}

const sizeClasses = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-2xl',
};

export function Modal({
  open,
  onClose,
  title,
  children,
  footer,
  size = 'md',
  scrollBody = false,
}: ModalProps) {
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* biome-ignore lint/a11y/useKeyWithClickEvents: #950 a mouse-only convenience; the keyboard closes this with Escape (document keydown handler above) */}
      <div
        className="fixed inset-0 bg-black/40 backdrop-blur-sm"
        aria-hidden="true"
        onClick={onClose}
      />
      <div className="flex min-h-full items-center justify-center p-4">
        <div
          className={[
            'relative bg-canvas rounded-xl shadow-xl w-full',
            sizeClasses[size],
            scrollBody ? 'max-h-[90vh] flex flex-col overflow-hidden' : '',
          ]
            .filter(Boolean)
            .join(' ')}
          // biome-ignore lint/a11y/useSemanticElements: #950 role="dialog" on a positioned panel is the intended ARIA pattern; native <dialog> is hidden until opened and brings its own positioning
          role="dialog"
          aria-modal="true"
          aria-labelledby="modal-title"
        >
          <div className="flex items-center justify-between border-b px-6 py-4">
            <h2 id="modal-title" className="text-lg font-semibold text-ink">
              {title}
            </h2>
            <button
              type="button"
              onClick={onClose}
              className="text-steel hover:text-ink transition-colors"
              aria-label="Close"
            >
              <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path
                  fillRule="evenodd"
                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
          </div>
          <div className={scrollBody ? 'px-6 py-4 flex-1 min-h-0 flex flex-col' : 'px-6 py-4'}>
            {children}
          </div>
          {footer && <div className="border-t px-6 py-4 flex justify-end gap-2">{footer}</div>}
        </div>
      </div>
    </div>
  );
}
